import { err, ok } from 'neverthrow';
import { z } from 'zod/v3';
import {
  BaseAction,
  type ActionResult,
  type ApplicableContext,
  type ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { toast } from '@cotera/client/app/components/ui';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';

type Errors = { t: string; message?: string }[];

const retryFailedDatasetRunsParamsSchema = z.object({
  datasetId: z.string(),
  /** The workflows whose failed runs to retry. Empty means every one bound. */
  workflowIds: z.array(z.string()),
  /** How many failures the user is retrying, for the confirmation toast only. */
  count: z.number().int().nonnegative(),
});

type Params = z.infer<typeof retryFailedDatasetRunsParamsSchema>;

type RetryFailedDatasetRunsClient = {
  resource: {
    workflow: Pick<
      TenantedClient['resource']['workflow'],
      'retryFailedDatasetRuns'
    >;
  };
};

/**
 * Retries every failed run across a dataset's selected automations.
 *
 * The server does the work in a background saga that streams the failed set, so
 * this is fire-and-forget: it kicks the retry off and reports that it started,
 * and the grid moves each row off `failed` as its new attempt lands (through the
 * automations overlay reconcile), not from anything returned here. An empty
 * `workflowIds` retries every workflow bound to the dataset.
 */
export class RetryFailedDatasetRunsAction extends BaseAction<Params, Errors> {
  title = 'Retry failed runs';
  group = 'Dataset';
  icon = 'refresh-cw';
  inputSchema = retryFailedDatasetRunsParamsSchema;

  constructor(private readonly client: RetryFailedDatasetRunsClient) {
    super();
  }

  // Invoked directly from the dataset run panel with its params, not discovered
  // from a selection, so it is always applicable; the panel only shows the
  // control when there are failures to retry.
  override applicable(_context: ApplicableContext): boolean {
    return true;
  }

  override async execute(
    params: Params,
    _context: ExecuteContext
  ): Promise<ActionResult<Errors>> {
    const pending = toast.loading('Starting retry…');
    const result = await this.client.resource.workflow.retryFailedDatasetRuns({
      datasetId: params.datasetId,
      ...(params.workflowIds.length > 0 && {
        workflowIds: params.workflowIds,
      }),
    });
    if (result.isErr()) {
      const message =
        result.error.t === 'application'
          ? result.error.err.message ?? 'Could not retry the failed runs'
          : 'Could not retry the failed runs';
      pending.error(message);
      return err([{ t: 'error', message }]);
    }

    pending.success(
      `Retrying ${params.count.toLocaleString()} failed ${
        params.count === 1 ? 'run' : 'runs'
      } in the background — rows update as they finish`
    );
    return ok({});
  }
}
